class TestimonialDrift extends HTMLElement {
    connectedCallback() {
        if (this.motionConnected) return;
        this.motionConnected = true;
        this.onScroll = this.requestFrame.bind(this);
        this.onResize = this.measure.bind(this);
        this.onClick = this.handleClick.bind(this);
        this.onSectionLoad = this.handleSectionLoad.bind(this);
        window.addEventListener("scroll", this.onScroll, { passive: true });
        document.addEventListener("scroll", this.onScroll, { passive: true, capture: true });
        window.addEventListener("resize", this.onResize, { passive: true });
        document.addEventListener("shopify:section:load", this.onSectionLoad);
        this.addEventListener("click", this.onClick);
        this.refresh();
        this.contentObserver = new MutationObserver((mutations) => {
            const cardsChanged = mutations.some((mutation) =>
                [...mutation.addedNodes, ...mutation.removedNodes].some(
                    (node) =>
                        node instanceof Element &&
                        (node.matches("[data-testimonial-card], .testimonial-drift__sticky") ||
                            node.querySelector("[data-testimonial-card], .testimonial-drift__sticky"))
                )
            );
            if (cardsChanged) this.refresh();
        });
        this.contentObserver.observe(this, { childList: true, subtree: true });
    }
    disconnectedCallback() {
        this.motionConnected = false;
        window.removeEventListener("scroll", this.onScroll);
        document.removeEventListener("scroll", this.onScroll, { capture: true });
        window.removeEventListener("resize", this.onResize);
        document.removeEventListener("shopify:section:load", this.onSectionLoad);
        this.removeEventListener("click", this.onClick);
        this.contentObserver?.disconnect();
        this.resizeObserver?.disconnect();
        if (this.frameRequest) cancelAnimationFrame(this.frameRequest);
    }
    handleSectionLoad(event) {
        if (event.target === this || event.target?.contains(this)) this.refresh();
    }
    refresh() {
        this.removeAttribute("data-motion-ready");
        this.sticky = this.querySelector(".testimonial-drift__sticky");
        this.cardsContainer = this.querySelector(".testimonial-drift__cards");
        this.cards = Array.from(this.querySelectorAll("[data-testimonial-card]"));
        this.headings = Array.from(this.querySelectorAll("[data-heading-line]"));
        if (!this.sticky || !this.cardsContainer || !this.cards.length) return;
        this.assignLanes();
        this.resizeObserver?.disconnect();
        this.resizeObserver = new ResizeObserver(this.onResize);
        this.resizeObserver.observe(this.sticky);
        this.measure();
    }
    assignLanes() {
        const desktopLeft = [14, 44, 8, 48, 20, 42];
        const desktopWidth = [46, 44, 44, 46, 44, 44];
        const desktopMaxWidth = [580, 540, 550, 580, 540, 550];
        const starts = [105, 161, 217, 273, 329, 385];
        const drifts = [0.22, 0, -0.12, 0.22, 0, -0.12];
        this.cards.forEach((card, index) => {
            card.style.setProperty("--testimonial-left", `${desktopLeft[index % desktopLeft.length]}%`);
            card.style.setProperty("--testimonial-width", `${desktopWidth[index % desktopWidth.length]}vw`);
            card.style.setProperty("--testimonial-max-width", `${desktopMaxWidth[index % desktopMaxWidth.length]}px`);
            card.dataset.startViewport = String(starts[index % starts.length]);
            card.dataset.drift = String(drifts[index % drifts.length]);
        });
    }
    measure() {
        if (!this.sticky || !this.cards?.length) return;
        this.isDesktop = window.innerWidth >= 920 && !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        if (!this.isDesktop) {
            this.removeAttribute("data-motion-ready");
            this.style.height = "";
            this.cards.forEach((card) => (card.style.transform = ""));
            this.headings.forEach((heading) => (heading.style.transform = ""));
            return;
        }
        this.setAttribute("data-motion-ready", "");
        this.headings.forEach((heading) => (heading.style.transform = ""));
        this.viewportHeight = window.innerHeight;
        this.isCompactDesktop = window.innerWidth < 1500;
        const lastCard = this.cards.at(-1);
        const lastStart = ((Number.parseFloat(lastCard?.dataset.startViewport) || 100) / 100) * this.viewportHeight;
        const lastSpeed = this.isCompactDesktop ? 1 : 1 + (Number.parseFloat(lastCard?.dataset.drift) || 0);
        this.exitProgress = lastStart / lastSpeed;
        this.style.height = `${this.viewportHeight + this.exitProgress}px`;
        this.render();
    }
    requestFrame() {
        if (this.frameRequest || !this.isDesktop) return;
        this.frameRequest = requestAnimationFrame(() => {
            this.frameRequest = 0;
            this.render();
        });
    }
    render() {
        if (!this.isDesktop) return;
        const bounds = this.getBoundingClientRect();
        const progress = Math.max(-bounds.top, 0);
        this.cards.forEach((card) => {
            const start = ((Number.parseFloat(card.dataset.startViewport) || 100) / 100) * this.viewportHeight;
            const drift = this.isCompactDesktop ? 0 : Number.parseFloat(card.dataset.drift) || 0;
            card.style.transform = `translate3d(0, ${start - progress * (1 + drift)}px, 0)`;
        });
    }
    handleClick(event) {
        const arrow = event.target.closest("[data-testimonial-prev], [data-testimonial-next]");
        if (!arrow || !this.contains(arrow) || !this.cardsContainer) return;
        const card = this.cardsContainer.querySelector("[data-testimonial-card]");
        const gap = Number.parseFloat(getComputedStyle(this.cardsContainer).columnGap) || 12;
        const amount = (card?.getBoundingClientRect().width || this.cardsContainer.clientWidth * 0.86) + gap;
        const direction = arrow.hasAttribute("data-testimonial-prev") ? -1 : 1;
        if (this.cardsContainer.scrollBy) {
            this.cardsContainer.scrollBy({ left: amount * direction, behavior: "smooth" });
        } else {
            this.cardsContainer.scrollLeft += amount * direction;
        }
    }
}
if (!customElements.get("testimonial-drift")) {
    customElements.define("testimonial-drift", TestimonialDrift);
}
